import { useMemo, useState } from 'react';
import { useChargePoints, useUsage } from '../lib/queries';
import type { UsageBucket, UsageSession } from '../data/live';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { loadFailedHint } from '../lib/errors';

/*
 * Usage over a window: sessions, energy, time.
 *
 * Written for the test phase, where the question is "did it work, how often,
 * and on what" rather than "what did we earn". Two rules it exists to hold.
 *
 * It never prints a currency figure it does not have. No charge point has a
 * tariff yet, so every session's cost is null, and a table of 0.00 would read
 * as "earned nothing" instead of "never priced".
 *
 * And it reports the sessions that would wreck an average rather than quietly
 * dropping them. One 47-hour session in a set of eight is most of the total
 * time; an average that hides it is worse than no average.
 */

type Range = 'all' | 'today' | '7d' | '30d';

const RANGES: Array<{ key: Range; label: string }> = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
];

function windowFor(r: Range): string | undefined {
  if (r === 'all') return undefined;
  const d = new Date();
  if (r === 'today') d.setHours(0, 0, 0, 0);
  else d.setDate(d.getDate() - (r === '7d' ? 7 : 30));
  return d.toISOString();
}

/** Seconds as something a person reads at a glance. */
function duration(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  const h = Math.floor(sec / 3600);
  return h < 48 ? `${h}h ${Math.round((sec % 3600) / 60)}m` : `${(sec / 86400).toFixed(1)}d`;
}

/** kWh at the precision the meter actually gives. A simulator delivering eight
 *  watt-hours should not be rounded to 0.0 and called a session with no energy. */
const kwh = (v: number) => (v >= 1 ? v.toFixed(2) : v.toFixed(3));


/**
 * Share of the window a bay spent in a session.
 *
 * Measured against wall-clock time in the window, including every hour the
 * charge point was unreachable. That is the number an operator wants for "is
 * this site earning its install" — but it is NOT uptime-adjusted, because
 * nothing stores when a charger was reachable. A unit that was offline for six
 * days reads as badly utilised, which is true, and does not say why.
 *
 * Divided by connector count, so a two-bay unit with both bays busy for an hour
 * reads as 100% for that hour rather than 200%.
 */
function utilisation(seconds: number, windowSeconds: number, bays: number): number | null {
  if (!windowSeconds || bays < 1) return null;
  return Math.min(100, (seconds / (windowSeconds * bays)) * 100);
}

const pct = (v: number | null) => (v == null ? '—' : v < 0.1 ? '<0.1%' : `${v.toFixed(1)}%`);

function BucketTable({ title, hint, rows, unit, windowSeconds, baysOf, onPick, picked }: {
  title: string; hint?: string; rows: UsageBucket[]; unit: string;
  windowSeconds?: number;
  baysOf?: (key: string) => number;
  onPick?: (key: string) => void;
  picked?: string | null;
}) {
  const max = Math.max(1, ...rows.map((r) => r.sessions));
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="li-title">{title}</div>
      {hint && <div className="li-sub" style={{ marginBottom: 6 }}>{hint}</div>}
      {!rows.length ? (
        <div className="li-sub faint" style={{ marginTop: 6 }}>Nothing in this window.</div>
      ) : (
        <div className="tbl" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>{unit}</th>
                <th className="num">Sessions</th>
                <th className="num">kWh</th>
                <th className="num">Time</th>
                {windowSeconds != null && <th className="num">Utilisation</th>}
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.key}
                    className={picked === b.key ? 'on' : undefined}
                    style={onPick ? { cursor: 'pointer' } : undefined}
                    onClick={onPick ? () => onPick(b.key) : undefined}>
                  <td>
                    {onPick
                      ? <a role="button" tabIndex={0}
                           onKeyDown={(e) => { if (e.key === 'Enter') onPick(b.key); }}
                           style={{ textDecoration: 'underline', cursor: 'pointer' }}>{b.label}</a>
                      : b.label}
                    {b.open > 0 && <Pill tone="mid">{b.open} open</Pill>}
                  </td>
                  <td className="num mono">{b.sessions}</td>
                  <td className="num mono">{kwh(b.kwh)}</td>
                  <td className="num mono">{duration(b.timed ? b.seconds : null)}</td>
                  {windowSeconds != null && (
                    <td className="num mono">
                      {pct(utilisation(b.seconds, windowSeconds, baysOf ? baysOf(b.key) : 1))}
                    </td>
                  )}
                  <td>
                    <span style={{
                      display: 'inline-block', height: 6, borderRadius: 3,
                      background: 'var(--primary)', verticalAlign: 'middle',
                      width: `${Math.max(2, (b.sessions / max) * 90)}px`,
                    }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function Usage() {
  const [range, setRange] = useState<Range>('all');
  const [picked, setPicked] = useState<string | null>(null);
  const { data, isLoading, error } = useUsage(windowFor(range));
  // Connector counts, so utilisation is per bay rather than per cabinet.
  const { data: cpRes } = useChargePoints();

  const sorted = useMemo<UsageSession[]>(
    () => [...(data?.sessions ?? [])].sort((a, b) =>
      (b.startTime ?? '').localeCompare(a.startTime ?? '')),
    [data],
  );

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="The usage report is not available." hint={loadFailedHint(error)} />;

  const t = data.totals;
  const a = data.anomalies;
  const longSeconds = 12 * 3600;
  // Time excluding the outliers, so there is a mean worth reading beside the
  // raw total rather than instead of it.
  const settled = sorted.filter((s) => !s.isActive && s.seconds != null && s.seconds <= longSeconds);
  const typical = settled.length
    ? settled.reduce((n, s) => n + (s.seconds ?? 0), 0) / settled.length
    : null;

  /* The window in seconds, which utilisation divides by. For a named range it
     is the range; for "all time" it runs from the first session ever recorded
     to now, because utilisation before there was anything to utilise is not a
     number anybody wants. */
  const startedAt = windowFor(range) ?? t.firstAt;
  const windowSeconds = startedAt
    ? Math.max(1, (Date.now() - Date.parse(startedAt)) / 1000)
    : 0;

  const baysOfCp = (key: string) => {
    const cp = cpRes?.items.find((c) => c.id === key);
    return Math.max(1, cp?.connectors.length ?? 1);
  };

  const detail = picked ? data.byChargePoint.find((b) => b.key === picked) ?? null : null;
  const detailSessions = picked ? sorted.filter((x) => x.chargePointId === picked) : [];
  const detailConnectors = picked
    ? data.byConnector.filter((b) => b.key.startsWith(`${picked}#`))
    : [];

  return (
    <>
      <div className="toolbar">
        <div className="chips">
          {RANGES.map((r) => (
            <Chip key={r.key} label={r.label} selected={range === r.key}
                  onClick={() => setRange(r.key)} />
          ))}
        </div>
        <div className="li-sub">
          {t.firstAt
            ? `${new Date(t.firstAt).toLocaleDateString()} — ${new Date(t.lastAt!).toLocaleDateString()}`
            : 'no sessions in this window'}
        </div>
      </div>

      <div className="tiles">
        <Tile k="Sessions" v={t.sessions} d={t.open ? `${t.open} still open` : 'all closed'} />
        <Tile k="Energy" v={kwh(t.kwh)} d="kWh delivered" />
        <Tile k="Charging time" v={duration(t.seconds)} d={`over ${t.timedSessions} timed sessions`} />
        <Tile k="Typical session" v={duration(typical)} d="excluding open and outliers" />
        <Tile k="Charge points" v={t.chargePoints} d={`${t.idTags} idTag${t.idTags === 1 ? '' : 's'} used`} />
        <Tile k="Drivers" v={t.drivers}
              d={t.drivers ? 'with sessions in this window' : 'none attributable'} />
      </div>

      {/* Money is deliberately absent from the tiles above. Saying 0.00 where
          nothing was ever priced is a claim about revenue rather than about
          configuration. */}
      <div className="note" style={{ marginTop: 12 }}>
        {t.pricedSessions === 0 ? (
          <>
            <b>No session in this window has been priced.</b> None of these charge
            points has a tariff set — so this is "never priced", not "earned
            nothing". Set one on a charge point's Edit dialog.
          </>
        ) : t.mixedCurrencies ? (
          <>
            <b>{t.pricedSessions} of {t.sessions} sessions priced</b>, in more than
            one currency — so no total is shown, because adding them would produce
            a number that means nothing. Narrow the window to one charge point,
            or price the network in a single currency.
          </>
        ) : (
          <>
            <b>{t.pricedSessions} of {t.sessions} sessions priced</b>, totalling{' '}
            {t.cost?.toFixed(2)}{t.currency ? ` ${t.currency}` : ''}. The rest ran
            on charge points with no tariff.
            <div className="li-sub" style={{ marginTop: 6 }}>
              Figures marked <span className="mono">*</span> were worked out from
              the charge point's <b>current</b> tariff, because the CSMS never
              priced the session. Change a tariff and that history re-prices with
              it — nothing records the rate that was in force at the time.
            </div>
          </>
        )}
      </div>

      {t.unattached > 0 && (
        <div className="note" style={{ marginTop: 12 }}>
          <b>{t.unattached} of {t.sessions} sessions cannot be billed to anyone.</b>{' '}
          They ran on idTags that exist in the CSMS but map to no driver here — a
          card loaded straight into CitrineOS, or a tag authorized from the test
          bench. The energy was delivered and there is nobody to charge for it.
          A tag issued through Drivers, by adding a vehicle, carries the driver
          with it.
        </div>
      )}

      {(!!a.open.length || !!a.veryLong.length || !!a.zeroEnergy.length) && (
        <div className="note" style={{ marginTop: 12 }}>
          <b>Worth knowing before reading the numbers above.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {!!a.open.length && (
              <li>
                Still open: {a.open.map((id) => `#${id}`).join(', ')}. Counted in
                sessions, but contributing no end time, so they are outside the
                timed figures.
              </li>
            )}
            {a.veryLong.map((v) => (
              <li key={v.transactionId}>
                Session #{v.transactionId} ran <b>{v.hours} hours</b>. It is
                included above and dominates the total time — a charger left in a
                session rather than a charge that long.
              </li>
            ))}
            {!!a.zeroEnergy.length && (
              <li>
                Delivered no energy: {a.zeroEnergy.map((id) => `#${id}`).join(', ')}.
              </li>
            )}
            {a.meterReversed.map((m) => (
              <li key={m.transactionId}>
                Session #{m.transactionId} reported{' '}
                <b>{m.reported.toFixed(3)} kWh</b> — the charge point's meter read
                lower at the end than at the start, so its register was reset
                mid-session. Almost always a power cut on a charger that keeps
                the register in RAM. The energy delivered is unknown, not zero,
                and it is excluded from every total above rather than subtracted
                from them.
              </li>
            ))}
          </ul>
        </div>
      )}

      <BucketTable
        title="By charge point"
        hint="Click one to see its utilisation and every session on it."
        rows={data.byChargePoint}
        unit="Charge point"
        windowSeconds={windowSeconds}
        baysOf={baysOfCp}
        onPick={(k) => setPicked(k === picked ? null : k)}
        picked={picked}
      />

      {detail && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="li-title">{detail.label}</div>
            <button className="btn ghost sm" onClick={() => setPicked(null)}>Close</button>
          </div>

          <div className="tiles" style={{ marginTop: 10 }}>
            <Tile k="Utilisation"
                  v={pct(utilisation(detail.seconds, windowSeconds, baysOfCp(detail.key)))}
                  d={`of ${duration(windowSeconds)} × ${baysOfCp(detail.key)} bay${baysOfCp(detail.key) === 1 ? '' : 's'}`} />
            <Tile k="Sessions" v={detail.sessions} d={detail.open ? `${detail.open} open` : 'all closed'} />
            <Tile k="Energy" v={kwh(detail.kwh)} d="kWh delivered" />
            <Tile k="In session" v={duration(detail.seconds)} d="total across bays" />
            <Tile k="Last used" v={detail.last ? new Date(detail.last).toLocaleDateString() : '—'}
                  d={detail.first ? `first ${new Date(detail.first).toLocaleDateString()}` : ''} />
          </div>

          <div className="note" style={{ marginTop: 12 }}>
            <b>Utilisation is measured against wall-clock time in this window</b>,
            including every hour the unit was unreachable. Nothing records when a
            charge point was online, so a unit that was dark for a week reads as
            badly utilised — which is true, and does not say why. The Charge
            points screen says why.
          </div>

          {detailConnectors.length > 1 && (
            <div className="tbl" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Connector</th><th className="num">Sessions</th>
                    <th className="num">kWh</th><th className="num">Time</th>
                    <th className="num">Utilisation</th>
                  </tr>
                </thead>
                <tbody>
                  {detailConnectors.map((c) => (
                    <tr key={c.key}>
                      <td>{c.label}</td>
                      <td className="num mono">{c.sessions}</td>
                      <td className="num mono">{kwh(c.kwh)}</td>
                      <td className="num mono">{duration(c.timed ? c.seconds : null)}</td>
                      <td className="num mono">{pct(utilisation(c.seconds, windowSeconds, 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="li-title" style={{ marginTop: 14, fontSize: 13 }}>
            Every session on this charge point
          </div>
          <div className="tbl" style={{ marginTop: 6 }}>
            <table>
              <thead>
                <tr>
                  <th>#</th><th className="num">C</th><th>Driver</th><th>idTag</th>
                  <th>Started</th><th>Ended</th><th className="num">Duration</th>
                  <th className="num">kWh</th><th className="num">Cost</th>
                  <th className="num">Share of use</th>
                  <th>Ended by</th>
                </tr>
              </thead>
              <tbody>
                {detailSessions.map((x) => (
                  <tr key={x.transactionId}>
                    <td className="mono">{x.transactionId}</td>
                    <td className="num mono">{x.connectorId ?? '—'}</td>
                    <td>{x.driver ?? <span className="faint">unattached</span>}</td>
                    <td className="mono">{x.idTag ?? <span className="faint">—</span>}</td>
                    <td className="li-sub">
                      {x.startTime ? new Date(x.startTime).toLocaleString() : '—'}
                    </td>
                    {/* A session still running has no end time, and printing
                        one would be inventing it. */}
                    <td className="li-sub">
                      {x.isActive ? <span className="faint">running</span>
                        : x.endTime ? new Date(x.endTime).toLocaleString()
                          : <span className="faint">—</span>}
                    </td>
                    <td className="num mono">
                      {x.isActive ? <Pill tone="mid">open</Pill> : duration(x.seconds)}
                    </td>
                    <td className="num mono">
                      {x.meterReversed
                        ? <span style={{ color: 'var(--bad)' }}>meter reset</span>
                        : kwh(x.kwh)}
                    </td>
                    {/* Null is "no tariff on this charge point", which is not
                        the same as free. A figure worked out from the tariff
                        rather than billed by the CSMS is marked, because those
                        are different claims. */}
                    <td className="num mono">
                      {x.cost == null
                        ? <span className="faint" title="This charge point has no tariff set">not priced</span>
                        : (
                          <span title={x.costFromCsms
                            ? 'Billed by the CSMS'
                            : "Energy at this charge point's current tariff — the CSMS did not price it"}>
                            {x.cost.toFixed(2)}
                            {x.currency ? ` ${x.currency}` : ''}
                            {!x.costFromCsms && <span className="faint">*</span>}
                          </span>
                        )}
                    </td>
                    {/* This session as a share of everything this unit did. */}
                    <td className="num mono">
                      {detail.seconds && x.seconds != null
                        ? `${((x.seconds / detail.seconds) * 100).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td>
                      {x.isActive ? <span className="faint">—</span>
                        : x.stoppedReason ?? <span className="faint">not stated</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BucketTable title="By connector" rows={data.byConnector} unit="Connector"
                   windowSeconds={windowSeconds} />
      <BucketTable
        title="By driver"
        hint="One driver charging on several cars is one row here. This is the shape a bill takes."
        rows={data.byDriver}
        unit="Driver"
      />
      <BucketTable
        title="By vehicle"
        hint="The same driver's cars, separately — which car used what."
        rows={data.byVehicle}
        unit="Vehicle"
      />
      <BucketTable
        title="By idTag"
        hint="The credential itself. A tag marked unattached belongs to no driver, so its sessions are unbillable."
        rows={data.byIdTag}
        unit="idTag"
      />
      <BucketTable title="By day" rows={data.byDay} unit="Day" />

      <div className="card" style={{ marginTop: 14 }}>
        <div className="li-title">Every session</div>
        <div className="li-sub" style={{ marginBottom: 6 }}>
          Newest first. Energy comes from the charger's own meter.
        </div>
        {!sorted.length ? (
          <Empty title="No sessions in this window." />
        ) : (
          <div className="tbl" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Charge point</th><th className="num">C</th>
                  <th>Driver</th><th>Vehicle</th><th>idTag</th>
                  <th>Started</th><th className="num">Duration</th>
                  <th className="num">kWh</th><th>Ended by</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.transactionId}>
                    <td className="mono">{s.transactionId}</td>
                    <td>{s.chargePoint}</td>
                    <td className="num mono">{s.connectorId ?? '—'}</td>
                    <td>{s.driver ?? <span className="faint">unattached</span>}</td>
                    <td>
                      {s.vehicle
                        ? <>{s.vehicle}{s.plate && <span className="li-sub"> {s.plate}</span>}</>
                        : <span className="faint">—</span>}
                    </td>
                    <td className="mono">{s.idTag ?? <span className="faint">—</span>}</td>
                    <td className="li-sub">
                      {s.startTime ? new Date(s.startTime).toLocaleString() : '—'}
                    </td>
                    <td className="num mono">
                      {s.isActive ? <Pill tone="mid">open</Pill> : duration(s.seconds)}
                    </td>
                    <td className="num mono">
                      {s.meterReversed
                        ? <span title={`the charger reported ${s.reportedKwh} kWh`}
                                style={{ color: 'var(--bad)' }}>meter reset</span>
                        : kwh(s.kwh)}
                    </td>
                    <td>
                      {s.isActive ? <span className="faint">—</span>
                        : s.stoppedReason ?? <span className="faint">not stated</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* A column the CSMS does not fill on 1.6. Said out loud, because a
          silent zero would be read as "nobody used the app". */}
      {t.sessions > 0 && t.remoteStarted === 0 && (
        <div className="note" style={{ marginTop: 12 }}>
          <b>Remote-start attribution is not available on OCPP 1.6.</b> CitrineOS
          leaves <span className="mono">remoteStartId</span> null on 1.6
          transactions, so a session started from the console or the driver app
          cannot be told from a card presented at the unit. Sessions started by
          RemoteStartTransaction during this window are in here, counted, and
          simply not labelled as such — the evidence is in OCPP messages.
        </div>
      )}
    </>
  );
}
